import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { err } from "./hrUtils";

/** Add a person: start from a team login, a payroll employee, or blank. Creates the HR record and opens it. */
export function AddEmployeeDialog({ open, onClose, meta, prefill, onCreated }) {
  const nav = useNavigate();
  const [source, setSource] = useState("team");
  const [team, setTeam] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [form, setForm] = useState({ user_id: "", payroll_employee_id: "", first_name: "", last_name: "", email: "", job_title: "", department: "", start_date: "", contract_type: "permanent", status: "active" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    API.get("/hr/team-candidates").then((r) => setTeam(r.data.users)).catch(() => setTeam([]));
    API.get("/hr/payroll-candidates").then((r) => setPayroll(r.data.employees)).catch(() => setPayroll([]));
    const [first = "", ...rest] = (prefill?.name || "").split(" ");
    setForm({ user_id: prefill?.user_id || "", payroll_employee_id: "", first_name: first, last_name: rest.join(" "), email: "", job_title: "", department: "", start_date: "", contract_type: "permanent", status: "active" });
    setSource(prefill?.user_id ? "team" : "team");
  }, [open, prefill]);

  const pickUser = (uid) => {
    const u = team.find((x) => x.user_id === uid);
    const [first = "", ...rest] = (u?.name || "").split(" ");
    setForm((f) => ({ ...f, user_id: uid, payroll_employee_id: "", first_name: uid ? first : f.first_name, last_name: uid ? rest.join(" ") : f.last_name, email: u?.email || f.email }));
  };
  const pickPayroll = (pid) => {
    const p = payroll.find((x) => x.id === pid);
    setForm((f) => ({ ...f, payroll_employee_id: pid, first_name: p?.first_name || f.first_name, last_name: p?.last_name || f.last_name, email: p?.email || f.email }));
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name are required"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (source !== "team") body.user_id = "";
      if (source !== "payroll") body.payroll_employee_id = "";
      const r = await API.post("/hr/employees", body);
      toast.success(r.data.message);
      onCreated?.();
      onClose();
      nav(`/hr/people/${r.data.id}`);
    } catch (e) { err(e, "Couldn't add that person"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="hr-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a person</DialogTitle>
          <DialogDescription>Start from their login or payroll record so nothing is typed twice, or add someone new.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1">
          {[["team", `From a team login${team.length ? ` (${team.length})` : ""}`], ["payroll", `From payroll${payroll.length ? ` (${payroll.length})` : ""}`], ["blank", "Someone new"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSource(k)} className={`px-2.5 py-1 rounded-md text-xs border ${source === k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`hr-add-source-${k}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-3">
          {source === "team" && (
            <div>
              <Label className="text-xs">Team member</Label>
              <select value={form.user_id} onChange={(e) => pickUser(e.target.value)} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-add-user">
                <option value="">Choose a login…</option>
                {team.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} — {u.role.replace("_", " ")} ({u.email})</option>)}
              </select>
              {team.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Every login already has a record.</p>}
            </div>
          )}
          {source === "payroll" && (
            <div>
              <Label className="text-xs">Payroll employee</Label>
              <select value={form.payroll_employee_id} onChange={(e) => pickPayroll(e.target.value)} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-add-payroll">
                <option value="">Choose a payroll record…</option>
                {payroll.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.payroll_id}{p.active === false ? " (inactive)" : ""}</option>)}
              </select>
              {payroll.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Every payroll employee is already linked.</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First name *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1" data-testid="hr-add-first" /></div>
            <div><Label className="text-xs">Last name *</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1" data-testid="hr-add-last" /></div>
            <div><Label className="text-xs">Work e-mail</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="hr-add-email" /></div>
            <div><Label className="text-xs">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1" data-testid="hr-add-start" /></div>
            <div><Label className="text-xs">Job title</Label><Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1" data-testid="hr-add-title" /></div>
            <div>
              <Label className="text-xs">Department</Label>
              <select value={form.department} onChange={(e) => set("department", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-add-department">
                <option value="">—</option>
                {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Contract</Label>
              <select value={form.contract_type} onChange={(e) => set("contract_type", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-add-contract">
                {Object.entries(meta?.contract_types || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="hr-add-status">
                <option value="onboarding">Onboarding (not started yet)</option>
                <option value="active">Active</option>
              </select>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="hr-add-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="hr-add-submit">{busy ? "Adding…" : "Add person"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
